import React, { useMemo } from 'react';
import { FmKeyFigures, type FmKeyFigure } from './FmKeyFigures';
import { FmChip } from './FmChip';
import { FmHeading, FmText } from './FmComposition';
import { FmRowActions, type FmRowAction } from './FmRowActions';
import { fmIconMap } from './icons';
import { SeverityBadge } from './FmRiskWorkspace.regions';
import type { FmRiskAction, FmRiskDetail, FmRiskIconId } from './FmRiskWorkspace.types';

const resolveIcon = (iconId?: FmRiskIconId): React.ReactNode =>
  iconId ? fmIconMap[iconId] : undefined;

const adaptActions = (actions: FmRiskAction[]): FmRowAction[] =>
  actions.map((action) => ({
    id: action.id,
    label: action.label,
    intent: action.intent,
    icon: resolveIcon(action.iconId),
    disabledReason: action.disabledReason,
    confirm: action.confirm,
    onSelect: action.onSelect,
    'data-testid': action.testId,
  }));

const DetailCard: React.FC<{
  detail: FmRiskDetail;
  actionsLabel: string;
  overflowLabel: string;
  testId?: string;
}> = ({ detail, actionsLabel, overflowLabel, testId }) => {
  const facts = useMemo<FmKeyFigure[]>(
    () =>
      (detail.facts ?? []).map((fact) => ({
        id: fact.label,
        label: fact.label,
        value: fact.value,
        unit: fact.unit,
      })),
    [detail.facts],
  );
  return (
    <div
      className="fm-risk-workspace__detail-card"
      data-testid={testId ? `${testId}-card` : undefined}
    >
      <div className="fm-risk-workspace__detail-head">
        <FmHeading level={4} visualLevel={4}>
          {detail.title}
        </FmHeading>
        <SeverityBadge
          severity={detail.severity}
          label={detail.severityLabel ?? detail.severity}
          testId={testId ? `${testId}-severity` : undefined}
        />
      </div>
      {detail.freshness ? (
        <div className="fm-risk-workspace__status-labels">
          <FmChip variant="mute">{detail.freshness}</FmChip>
        </div>
      ) : null}
      {detail.summary ? (
        <FmText as="p" size="caption">
          {detail.summary}
        </FmText>
      ) : null}
      {facts.length > 0 ? <FmKeyFigures presentation="rail" items={facts} columns={2} /> : null}
      {detail.reasonNote ? (
        <FmText as="p" size="fine" tone="subtle">
          {detail.reasonNote}
        </FmText>
      ) : null}
      {detail.actions && detail.actions.length > 0 ? (
        <FmRowActions
          actions={adaptActions(detail.actions)}
          label={actionsLabel}
          overflowLabel={overflowLabel}
          data-testid={testId ? `${testId}-actions` : undefined}
        />
      ) : null}
    </div>
  );
};

export interface DetailPaneProps {
  detail?: FmRiskDetail;
  title?: string;
  emptyLabel?: string;
  actionsLabel: string;
  overflowLabel: string;
  testId?: string;
}

export const DetailPane: React.FC<DetailPaneProps> = ({
  detail,
  title,
  emptyLabel,
  actionsLabel,
  overflowLabel,
  testId,
}) => (
  <section className="fm-risk-workspace__detail" data-testid={testId} aria-label={title}>
    {title ? (
      <FmHeading level={3} visualLevel={4} className="fm-risk-workspace__region-title">
        {title}
      </FmHeading>
    ) : null}
    {detail ? (
      <DetailCard
        detail={detail}
        actionsLabel={actionsLabel}
        overflowLabel={overflowLabel}
        testId={testId}
      />
    ) : emptyLabel ? (
      <div
        className="fm-risk-workspace__empty"
        data-state="empty"
        data-testid={testId ? `${testId}-empty` : undefined}
      >
        {emptyLabel}
      </div>
    ) : null}
  </section>
);
